import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TemplateConfig, TemplateDetailView, TemplateStatus, TemplateSummaryView } from '@lp/contracts';
import { Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { errorText, fieldProblems, get, post } from '@/lib/api';
import { useIsSuperAdmin } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import { Badge, Button, Dialog, Empty, ErrorBox, Field, Input, Loading, PageHeader, Select } from '@/components/ui';

export const useTemplates = () => useQuery({ queryKey: ['templates'], queryFn: () => get<TemplateSummaryView[]>('/platform/templates') });

export function TemplateStatusBadge({ status }: { status: TemplateStatus }) {
  const t = useT();
  return <Badge tone={status === 'active' ? 'green' : 'gray'}>{t(status === 'active' ? 'Active' : 'Archived')}</Badge>;
}

/** starter company templates (Phase 6, docs/52): what a new company starts with — its products and settings */
export function TemplatesPage() {
  const t = useT();
  const nav = useNavigate();
  const superAdmin = useIsSuperAdmin();
  const list = useTemplates();
  const [creating, setCreating] = useState(false);
  return (
    <>
      <PageHeader
        title={t('Templates')}
        subtitle={t('What a new company starts with: its products and its settings. A company stays on its template version until it is moved.')}
        actions={
          superAdmin && (
            <Button icon={<Plus size={14} />} onClick={() => setCreating(true)}>
              {t('New template')}
            </Button>
          )
        }
      />
      <div className="rounded-lg border border-ink-200 bg-white">
        {list.isLoading ? (
          <div className="p-4">
            <Loading />
          </div>
        ) : list.error ? (
          <div className="p-4">
            <ErrorBox text={errorText(list.error)} />
          </div>
        ) : !list.data?.length ? (
          <Empty title={t('No template yet')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-xs font-medium text-ink-500">
                  <th className="px-4 py-2">{t('Template')}</th>
                  <th className="px-4 py-2">{t('Products')}</th>
                  <th className="px-4 py-2 text-right">{t('Settings')}</th>
                  <th className="px-4 py-2">{t('Version')}</th>
                  <th className="px-4 py-2 text-right">{t('Clients')}</th>
                  <th className="px-4 py-2">{t('State')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((x) => (
                  <tr key={x.code} onClick={() => nav(`/templates/${x.code}`)} className="cursor-pointer border-b border-ink-100 last:border-0 hover:bg-ink-50" data-testid={`template-row-${x.code}`}>
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-ink-900">{x.nameEn}</span> <span className="font-mono text-[11px] text-ink-500">{x.code}</span>
                      <span className="block text-xs text-ink-500">{x.nameKm}</span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{x.config?.products.map((p) => p.code).join(' ') || '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{x.config ? Object.keys(x.config.settings).length : '—'}</td>
                    <td className="px-4 py-2.5 text-xs">
                      {x.publishedVersion ? `v${x.publishedVersion}` : '—'}
                      {x.draftVersion && (
                        <span className="ml-1.5">
                          <Badge tone="blue">
                            {t('Draft')} v{x.draftVersion}
                          </Badge>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{x.clients}</td>
                    <td className="px-4 py-2.5">
                      <TemplateStatusBadge status={x.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {creating && list.data && <NewTemplate templates={list.data} onClose={() => setCreating(false)} />}
    </>
  );
}

function NewTemplate({ templates, onClose }: { templates: TemplateSummaryView[]; onClose: () => void }) {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [nameKm, setNameKm] = useState('');
  const [description, setDescription] = useState('');
  const copyable = templates.filter((x) => x.config);
  const [from, setFrom] = useState('');
  const create = useMutation({
    mutationFn: () => {
      const config: TemplateConfig = copyable.find((x) => x.code === from)?.config ?? { products: [], settings: {}, levels: [] };
      return post<TemplateDetailView>('/platform/templates', { code, nameEn, nameKm, ...(description.trim() ? { description } : {}), config });
    },
    onSuccess: (x) => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
      nav(`/templates/${x.code}/versions/1/edit`);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <Dialog
      title={t('New template')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form="new-template" disabled={!code || !nameEn || !nameKm || create.isPending}>
            {t('Create draft')}
          </Button>
        </>
      }
    >
      <form id="new-template" onSubmit={submit} className="space-y-3">
        <Field label={t('Code')}>
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={12} placeholder="KH" />
        </Field>
        <Field label={t('Name (English)')}>
          <Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} maxLength={80} />
        </Field>
        <Field label={t('Name (Khmer)')}>
          <Input value={nameKm} onChange={(e) => setNameKm(e.target.value)} maxLength={80} />
        </Field>
        <Field label={t('Description')}>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={400} />
        </Field>
        <Field label={t('Start as a copy of')}>
          <Select value={from} onChange={(e) => setFrom(e.target.value)} className="w-full">
            <option value="">{t('Empty')}</option>
            {copyable.map((x) => (
              <option key={x.code} value={x.code}>
                {x.nameEn} ({x.code})
              </option>
            ))}
          </Select>
        </Field>
        {create.error && <ErrorBox text={[errorText(create.error), ...fieldProblems(create.error)].join(' — ')} />}
      </form>
    </Dialog>
  );
}
